import React from "react";
import {
  compose,
  withProps,
  mapProps,
  withHandlers,
  withState
} from "recompose";
import { connect } from "react-redux";
import { updateMap } from "app/map/actions";
import { computeBounds } from "app/map/utils";
import { createHocBase, next, ids, capitalizeFirstLetter } from "app/utils";
import { Field, FieldArray, getFormSyncErrors, FormSection } from "redux-form";
import { bindActionCreators } from "redux";
import {
  AnchorButton,
  Classes,
  Colors,
  Icon,
  Intent,
  Menu,
  MenuItem,
  Popover,
  Position
} from "@blueprintjs/core";
import styled from "styled-components";
import geoJsonArea from "@mapbox/geojson-area";
import { withObjectsConfig } from "app/topics/hocs";
import { setMode } from "app/map/actions";
import { withTmpField } from "../hocs";
import OptionsInput from "./OptionsInput";
import * as forms from "app/forms";
import * as validate from "app/validate";
import LocationFeedback from "app/feedback/LocationFeedback";
import { showToast, dismissToast } from "app/layout/actions";
import { injectIntl, FormattedMessage } from "react-intl";

const enhance = compose(
  forms.reduxField,
  forms.interactive,
  forms.unlabelled
);
const InputField = enhance(forms.CustomInputGroup);

const createInputData = input => {
  let options = input.options || [];
  if (!Array.isArray(options)) {
    options = [options];
  }
  const data = {
    defaultValue: "",
    label: input.name,
    key: input.name,
    options: options.map(option => {
      return {
        value: option,
        label: option
      };
    })
  };
  return data;
};

const createInputList = config => {
  return config.inputs
    .filter(input => Boolean(input.name))
    .map(input => createInputData(input));
};

const withActions = compose(
  mapProps(props => {
    const { objectsConfig, objectsConfigList, ...rest } = props;
    return {
      inputList: (objectsConfigList || []).map(config => {
        return {
          type: config.type,
          title: config.name,
          inputs: createInputList(config)
        };
      }),
      ...rest
    };
  }),
  withTmpField,
  withState("inputs", "setInputs", ""),
  connect(state => ({
    syncErrors: getFormSyncErrors("proposal")(state)
  }))
);

const ObjectsPicker = props => {
  const { children, onClick, inputList, setInputs } = props;
  return (
    <Popover position={Position.BOTTOM}>
      {children}
      <Menu>
        {" "}
        {inputList.map(object => (
          <MenuItem
            text={object.title}
            key={object.type}
            onClick={() => {
              onClick({ type: object.type });
              setInputs(object.inputs);
            }}
          />
        ))}
      </Menu>
    </Popover>
  );
};

const withMapMode = compose(
  connect(
    null,
    dispatch => ({
      setMapMode: bindActionCreators(setMode, dispatch)
    })
  ),
  withProps(props => {
    const {
      tmpField: { type }
    } = props;
    let mode = "point";
    if (type === "polygon") {
      mode = "polygon";
    }
    if (type === "line") {
      mode = "line";
    }
    return { mode };
  }),
  Component =>
    class extends createHocBase(Component) {
      componentDidMount() {
        this.props.setMapMode(this.props.mode);
      }

      componentWillUnmount() {
        this.props.setMapMode();
      }
    }
);

const enhanceObjectFormSection = compose(
  connect(
    null,
    dispatch => ({
      showToast: message => dispatch(showToast(message)),
      dismissToast: () => dispatch(dismissToast())
    })
  ),
  withMapMode,
  injectIntl,
  Component =>
    class extends createHocBase(Component) {
      componentDidMount() {
        const { showToast, tmpField, inputs, intl } = this.props;
        // TODO show different Toasts
        if (inputs && inputs.length !== 0) {
          showToast(
            intl.formatMessage(
              {
                id: "toast.addobjectwithinputs",
                defaultMessage: "Please add a new {object} and select {inputs}"
              },
              {
                object: capitalizeFirstLetter(tmpField.type),
                inputs: inputs[0].key
              }
            )
          );
        } else {
          showToast(
            intl.formatMessage(
              {
                id: "toast.addobject",
                defaultMessage: "Please add a new {object}"
              },
              {
                inputs: capitalizeFirstLetter(tmpField.type)
              }
            )
          );
        }
      }
      componentDidUpdate(prevProps) {
        const { isValid, showToast, tmpField, intl } = this.props;

        if (isValid === prevProps.isValid) {
          return;
        }

        if (isValid) {
          showToast(
            intl.formatMessage(
              {
                id: "proposal.save.prompt",
                defaultMessage:
                  "Please click Confirm to save your proposal or add a new {object}"
              },
              {
                object: capitalizeFirstLetter(tmpField.type)
              }
            )
          );
        }
      }
      componentWillUnmount() {
        const { dismissToast } = this.props;
        dismissToast();
      }
    }
);

const ObjectFormSection = enhanceObjectFormSection(props => {
  const {
    intl,
    tmpField,
    inputs,
    isValid,
    resetTmpField,
    fields,
    mode
  } = props;
  return (
    <FormSection name={`tmp.${tmpField.id}`}>
      <InputField
        name={intl.formatMessage({
          id: "proposal.title",
          defaultMessage: "title"
        })}
        label={`${capitalizeFirstLetter(tmpField.type)} ${intl.formatMessage({
          id: "proposal.title",
          defaultMessage: "title"
        })}`}
      />
      {inputs.map(input => {
        const name = `inputs.${input.key}`;
        if (!input.options) return null;
        if (!Array.isArray(input.options) || input.options.length < 1) {
          return (
            <InputField
              type="text"
              name={name}
              key={input.key}
              label={input.label || input.key}
              validate={[validate.required]}
            />
          );
        }
        return (
          <OptionsInput
            name={name}
            key={input.key}
            data={input}
            validate={[validate.required]}
          />
        );
      })}
      <div>
        {mode === "point" && (
          <LocationField
            locationObject={tmpField}
            name="location"
            intl={intl}
          />
        )}
        {mode === "polygon" && (
          <PolygonLocationField name="location" intl={intl} />
        )}
        {mode === "line" && <LineLocationField name="location" intl={intl} />}
        {tmpField.location && (
          <LocationFeedbackField
            locationObject={tmpField}
            isOpen={true}
            noFeedback={!isValid}
            name="locationFeedback"
          />
        )}
        <AnchorButton
          iconName="add"
          className={Classes.MINIMAL}
          disabled={!isValid}
          onClick={() => {
            fields.push(tmpField);
            resetTmpField();
          }}
        >
          <FormattedMessage id="text.confirm" defaultMessage="Confirm" />
        </AnchorButton>
        <AnchorButton
          iconName="undo"
          className={Classes.MINIMAL}
          onClick={() => resetTmpField()}
        >
          <FormattedMessage id="text.cancel" defaultMessage="Cancel" />
        </AnchorButton>
      </div>
    </FormSection>
  );
});

const ObjectSubform = withActions(props => {
  const {
    tmpField,
    setTmpField,
    resetTmpField,
    inputList,
    setInputs,
    inputs,
    fields,
    syncErrors = {},
    intl
  } = props;
  const isValid = !("tmp" in syncErrors);
  return !tmpField ? (
    <div>
      <p>
        <FormattedMessage
          id="proposal.objects.text"
          defaultMessage="Objects in this proposal:"
        />
      </p>
      <ObjectsPicker
        setInputs={setInputs}
        onClick={obj => {
          const id = next(ids);
          setTmpField({ id, [id]: { ...obj, id } });
        }}
        inputList={inputList}
      >
        <AnchorButton iconName="add" className={Classes.MINIMAL}>
          <FormattedMessage
            id="proposal.objects.add"
            defaultMessage="Add a new object"
          />
        </AnchorButton>
      </ObjectsPicker>
    </div>
  ) : (
    <div>
      <p>
        <FormattedMessage
          id="proposal.objects.text"
          defaultMessage="Objects in this proposal:"
        />
      </p>
      {inputs && (
        <ObjectFormSection
          tmpField={tmpField}
          inputs={inputs}
          isValid={isValid}
          fields={fields}
          resetTmpField={resetTmpField}
          intl={intl}
        />
      )}
    </div>
  );
});

const LocationField = props => {
  const { intl, ...rest } = props;
  return (
    <Field
      {...rest}
      validate={[validate.required]}
      component={props => {
        const {
          meta: { valid },
          input: { value },
          locationObject: { inputs }
        } = props;
        const building = inputs && inputs.building;
        const title = inputs && inputs.title;
        let lon, lat;
        if (valid && value) {
          [lon, lat] = value.coordinates;
        }
        return (
          <p>
            <Icon iconName="map-marker" intent={Intent.PRIMARY} />
            {valid && value
              ? building
                ? `Building ${building} ${title}`
                : `${lon}, ${lat}`
              : intl.formatMessage({
                  id: "map.location.prompt",
                  defaultMessage: "Click on the map to set the location!"
                })}
          </p>
        );
      }}
    />
  );
};

const LineLocationField = props => {
  const { intl, ...rest } = props;
  return (
    <Field
      {...rest}
      validate={[validate.required]}
      component={props => {
        const {
          meta: { valid },
          input: { value }
        } = props;
        return (
          <p>
            <Icon iconName="pt-icon-trending-up" intent={Intent.PRIMARY} />{" "}
            {valid && value
              ? `You selected a line.`
              : intl.formatMessage({
                  id: "map.line.prompt",
                  defaultMessage: "Click on the map to draw a line!"
                })}
          </p>
        );
      }}
    />
  );
};

const PolygonLocationField = props => {
  const { intl, ...rest } = props;
  return (
    <Field
      {...rest}
      validate={[validate.required]}
      component={props => {
        const {
          meta: { valid },
          input: { value }
        } = props;
        let sqm;
        if (valid && value) {
          sqm = geoJsonArea.geometry(value);
          sqm = Math.round(sqm);
        }
        return (
          <p>
            <Icon iconName="pt-icon-polygon-filter" intent={Intent.PRIMARY} />{" "}
            {valid && value
              ? intl.formatMessage(
                  {
                    id: "shapearea.message",
                    defaultMessage: "Shape area is about {area} m²."
                  },
                  {
                    area: sqm
                  }
                )
              : intl.formatMessage({
                  id: "map.shape.prompt",
                  defaultMessage: "Click on the map to draw a shape!"
                })}
          </p>
        );
      }}
    />
  );
};

const LocationFeedbackField = props => {
  return <Field {...props} component={LocationFeedback} />;
};

const ObjWrapper = styled.div`
  display: flex;
  flex-direction: row;
  border: 1px solid ${Colors.GRAY5};
  border-radius: 5px;
  margin: 1ex 0;
  padding: 1ex;
    background-color ${Colors.LIGHT_GRAY5};
  cursor: pointer;
  & .object:hover {
    color: ${Colors.BLUE3}
  }
  & .remove {
    flex: 0;
  }
  & .remove:hover {
    color: ${Colors.RED3}
  }
  & .location-feedback {
    margin-top: 1ex;
    border-top: 1px solid #ccc;
    padding-top: 1ex;
  }
`;

const InputEntry = styled.div`
  padding: 0.2rem;
`;

const clickable = compose(
  connect(
    null,
    dispatch => ({
      updateMap: options => dispatch(updateMap(options))
    })
  ),
  withTmpField,
  withHandlers({
    onClick: props => () => {
      const {
        updateMap,
        object: { location }
      } = props;
      const bounds = computeBounds(location);
      updateMap({ bounds });
    }
  })
);

const Obj = clickable(props => {
  const { object, onClick, onDeleteClick } = props;
  return (
    <ObjWrapper>
      <div className="object" onClick={onClick}>
        <Icon iconName="map-marker" /> <strong>{object.title}</strong>
        {Object.keys(object.inputs || {}).map(key => (
          <InputEntry key={key}>
            {capitalizeFirstLetter(key)}:{" "}
            {capitalizeFirstLetter(object.inputs[key])}
          </InputEntry>
        ))}
        <LocationFeedback
          className="location-feedback"
          locationObject={object}
        />
      </div>
      <div className="remove">
        <Icon
          iconName="trash"
          onClick={onDeleteClick}
          title={`Delete object ${object.id}`}
        />
      </div>
    </ObjWrapper>
  );
});

const objs = compose(
  withTmpField,
  withObjectsConfig
);

export const Objects = injectIntl(
  objs(props => {
    const { intl, tmpField, fields, objectsConfigList, objectsConfig } = props;
    return (
      <div>
        <ObjectSubform
          fields={fields}
          objectsConfig={objectsConfig}
          objectsConfigList={objectsConfigList}
          intl={intl}
        />
        {!tmpField &&
          fields.map((field, index) => {
            const obj = fields.get(index);
            return (
              <Obj
                objectsConfig={objectsConfig}
                key={index}
                object={obj}
                onDeleteClick={() => fields.remove(index)}
              />
            );
          })}
      </div>
    );
  })
);

export const ObjectsField = props => {
  return (
    <FieldArray
      name="content.objects"
      topicId={props.topicId}
      component={Objects}
    />
  );
};



// WEBPACK FOOTER //
// ./src/app/proposals/fields/ObjectsField.js